/**
 * Portal v2 DMARC policy rollout (`/portal/policy`).
 *
 * Advise + verify, never auto-push: the portal SHOWS the customer the DMARC
 * TXT record (`current_dns_record`) and where to publish it (`dns_record_name`),
 * then tells them to publish it at their DNS provider and re-check DNS on the
 * Domains page. The portal never edits the customer's DNS itself.
 *
 * A rollout targets ONE domain. An org-wide session must therefore choose which
 * domain before this page means anything — when the scope is "All domains" and
 * the customer has more than one, we ask them to pick rather than silently
 * acting on whichever domain they happened to log in with.
 *
 * Self-service create + advance: with no active rollout the customer can start
 * one (default p=reject); otherwise they advance stage-by-stage. Safety gates
 * are enforced server-side — an advance may return 409 with a human-readable
 * reason, which we surface verbatim via toast.error so the customer knows what
 * to fix (e.g. pass rate / volume thresholds).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { Skeleton } from "@ddmarc/ui";
import { portalApi, type PortalRollout } from "@/lib/portal-api";
import { usePortalScope } from "@/lib/portal-scope-context";
import { toast } from "@/hooks/use-toast";
import { CardV2, SectionHeader, StatPill } from "@/components/primitives";
import { StageStepper } from "./portal-policy/rollout-stepper";
import { EmptyState } from "./portal-policy/empty-state";
import { DnsRecordCard } from "./portal-policy/dns-record-card";

const STATUS_TONE: Record<PortalRollout["status"], "good" | "warn" | "danger" | "info" | "neutral"> = {
  pending: "neutral",
  active: "info",
  paused: "warn",
  blocked: "danger",
  completed: "good",
  cancelled: "neutral",
};

export function PortalPolicyV2Page() {
  const queryClient = useQueryClient();
  const { actionableDomainId, selectedDomain, hasMultipleDomains } =
    usePortalScope();

  // actionableDomainId is null for a single-domain customer until the domain
  // list resolves; the API treats a missing domain_id as "the domain you logged
  // in with", so the request is still correct and the page never blocks on it.
  const { data, isLoading } = useQuery({
    queryKey: ["portal-rollout", actionableDomainId],
    queryFn: () => portalApi.getRollout({ domain_id: actionableDomainId }),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["portal-rollout"] });

  const createMutation = useMutation({
    // Default to the strongest end-state (p=reject); the gates pace the journey.
    mutationFn: () =>
      portalApi.createRollout(
        { target_policy: "reject" },
        { domain_id: actionableDomainId }
      ),
    onSuccess: () => {
      toast.success({ title: "Rollout started", description: "Publish the DNS record below to begin." });
      invalidate();
    },
    onError: (err: Error) => {
      toast.error({ title: "Could not start rollout", description: err.message });
    },
  });

  const advanceMutation = useMutation({
    mutationFn: () => portalApi.advanceRollout({ domain_id: actionableDomainId }),
    onSuccess: () => {
      toast.success({ title: "Advanced to next stage", description: "Publish the updated DNS record below." });
      invalidate();
    },
    onError: (err: Error) => {
      // Carries the server-side 409 gate reason (e.g. pass-rate / volume) verbatim.
      toast.error({ title: "Cannot advance yet", description: err.message });
    },
  });

  const header = (
    <SectionHeader
      title={
        <span className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          Policy
        </span>
      }
      subtitle={
        selectedDomain
          ? `Roll ${selectedDomain.domain} forward to DMARC enforcement, gate by gate.`
          : "Roll your domain forward to DMARC enforcement, gate by gate."
      }
    />
  );

  // Org-wide scope with several domains: a rollout is per-domain, so acting on
  // an arbitrary one would be a surprise. Ask first. hasMultipleDomains is
  // false while the domain list loads, so this never flashes for one domain.
  if (hasMultipleDomains && actionableDomainId === null) {
    return (
      <div className="space-y-3.5">
        {header}
        <CardV2
          title="Choose a domain"
          subtitle="Policy rollouts run one domain at a time."
        >
          <p className="text-[12.5px] text-muted-foreground">
            Pick a domain from the selector at the top of the page to view or
            start its DMARC rollout.
          </p>
        </CardV2>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3.5">
        {header}
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  // getRollout resolves null when no rollout is active.
  if (!data) {
    return (
      <div className="space-y-3.5">
        {header}
        <EmptyState
          onStart={() => createMutation.mutate()}
          isPending={createMutation.isPending}
        />
      </div>
    );
  }

  const rollout = data;

  return (
    <div className="space-y-3.5">
      {header}

      <CardV2
        title="Rollout progress"
        action={
          <StatPill tone={STATUS_TONE[rollout.status]}>
            {rollout.status} · {rollout.progress_percent}%
          </StatPill>
        }
      >
        <div className="space-y-3">
          <StageStepper stages={rollout.stages} />
          {rollout.last_pass_rate !== null && (
            <p className="text-[12px] text-muted-foreground">
              Last pass rate:{" "}
              <span className="font-semibold text-foreground">
                {rollout.last_pass_rate.toFixed(1)}%
              </span>
            </p>
          )}
        </div>
      </CardV2>

      {rollout.status === "blocked" && rollout.blocked_reason && (
        <CardV2 className="border-[var(--v2-danger)]">
          <div className="flex items-start gap-2 text-[12.5px] text-[var(--v2-danger)]">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <span className="font-semibold">Rollout blocked: </span>
              {rollout.blocked_reason}
            </span>
          </div>
        </CardV2>
      )}

      {rollout.status === "completed" ? (
        <CardV2 title="Enforcement complete">
          <div className="flex items-center gap-2 text-[13px] text-foreground">
            <CheckCircle2 className="h-4 w-4 text-[var(--v2-good)]" />
            Your domain is enforcing <code className="font-mono-data text-[11.5px]">p=reject</code>.
          </div>
        </CardV2>
      ) : (
        <DnsRecordCard
          rollout={rollout}
          onAdvance={() => advanceMutation.mutate()}
          isAdvancing={advanceMutation.isPending}
        />
      )}
    </div>
  );
}
